import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';

import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import SessionsCard from './SessionsCard';
import { ShellContext } from './layout/AppShell';
import { SectionCard, StatCard, EmptyState, ConfirmDialog, currency, number } from './ui';
import { TRUE_COLORS } from '../theme';

type Draft = { name: string; lastname: string; phone: string; role: string; is_admin: boolean };

const MemberDetail = () => {
  const { id } = useParams();
  const axios = useAxios();
  const navigate = useNavigate();
  const { membership: me } = React.useContext(ShellContext);

  const [member, setMember] = React.useState<any>(null);
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [receipts, setReceipts] = React.useState<any[]>([]);
  const [notFound, setNotFound] = React.useState(false);
  const [message, setMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmRemove, setConfirmRemove] = React.useState(false);

  const toDraft = (m: any): Draft => ({ name: m.name ?? '', lastname: m.lastname ?? '', phone: m.phone ?? '', role: m.role ?? '', is_admin: !!m.is_admin });

  React.useEffect(() => {
    axios.get(`management/members/${id}/`)
      .then(({ data }) => {
        setMember(data);
        setDraft(toDraft(data));
        // Receipts store the cashier's *user* id, not the membership id.
        return axios.get('api/receipts/', {
          params: { cashier: data.member?.id, start_date: dayjs().subtract(30, 'day').utc().format(), end_date: dayjs().utc().format() },
        });
      })
      .then(r => r && setReceipts([...(r.data ?? [])].sort((a, b) => dayjs(b.purchase_date).diff(dayjs(a.purchase_date)))))
      .catch(() => setNotFound(true));
  }, [axios, id]);

  if (notFound) {
    return (
      <PageShell back={{ to: '/admin/organization', label: 'Organización' }}>
        <EmptyState title="Miembro no encontrado" description="Solo los administradores pueden ver los detalles del equipo." />
      </PageShell>
    );
  }

  const isSelf = me?.id === member?.id;
  const canEdit = !!me?.is_patron; // the API only lets owners edit members
  const dirty = member && draft && JSON.stringify(toDraft(member)) !== JSON.stringify(draft);
  const total = receipts.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
  const name = member ? ([member.name, member.lastname].filter(Boolean).join(' ') || member.login || member.member?.username) : '';
  const set = (k: keyof Omit<Draft, 'is_admin'>) => (e: React.ChangeEvent<HTMLInputElement>) => setDraft(d => d && { ...d, [k]: e.target.value });

  const save = async () => {
    if (!draft) return;
    try {
      const payload: any = { name: draft.name.trim(), lastname: draft.lastname.trim(), phone: draft.phone.trim(), role: draft.role.trim() };
      if (draft.is_admin !== member.is_admin) payload.is_admin = draft.is_admin;
      const { data } = await axios.put(`management/members/${id}/`, payload);
      setMember(data);
      setDraft(toDraft(data));
      setMessage({ type: 'success', text: 'Cambios guardados.' });
    } catch (err: any) {
      const d = err?.response?.data;
      setMessage({ type: 'error', text: typeof d === 'string' ? d : d && typeof d === 'object' ? Object.values(d).flat().join(' ') : 'No se pudieron guardar los cambios.' });
    }
  };

  return (
    <PageShell
      back={{ to: '/admin/organization', label: 'Organización' }}
      title={
        member ? (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Avatar sx={{ width: 48, height: 48, fontWeight: 700, bgcolor: TRUE_COLORS.primary }}>{name.slice(0, 2).toUpperCase()}</Avatar>
            <span>{name}</span>
          </Stack>
        ) : <Skeleton width={240} />
      }
      subtitle={
        member && (
          <Stack direction="row" spacing={1} component="span" sx={{ alignItems: 'center' }}>
            {member.is_patron ? <Chip size="small" label="Dueño" sx={{ bgcolor: TRUE_COLORS.primary, color: '#fff' }} />
              : member.is_admin ? <Chip size="small" label="Administrador" /> : <Chip size="small" variant="outlined" label="Cajero" />}
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>{member.member?.email || member.login || member.member?.username}</Typography>
          </Stack>
        )
      }
      actions={canEdit && (
        <>
          {dirty && <Button color="inherit" onClick={() => setDraft(toDraft(member))}>Descartar</Button>}
          <Button variant="contained" onClick={save} disabled={!dirty}>Guardar cambios</Button>
        </>
      )}
    >
      {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 3 }}>{message.text}</Alert>}
      <Grid container spacing={3}>
        {[
          { label: 'Ventas (30 días)', value: number(receipts.length), icon: <ReceiptLongOutlinedIcon /> },
          { label: 'Ingresos (30 días)', value: currency(total), icon: <PaymentsOutlinedIcon /> },
          { label: 'Ticket promedio', value: currency(receipts.length ? total / receipts.length : 0), icon: <ShoppingBasketOutlinedIcon /> },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, sm: 4 }}>
            <StatCard label={card.label} value={card.value} icon={card.icon} loading={!member} />
          </Grid>
        ))}

        <Grid size={{ xs: 12, lg: 5 }}>
          <Stack spacing={3}>
            <SectionCard title="Información">
              {draft ? (
                <Grid container spacing={2}>
                  <Grid size={6}><TextField label="Nombre" fullWidth value={draft.name} onChange={set('name')} disabled={!canEdit} /></Grid>
                  <Grid size={6}><TextField label="Apellido" fullWidth value={draft.lastname} onChange={set('lastname')} disabled={!canEdit} /></Grid>
                  <Grid size={12}><TextField label="Teléfono" type="tel" fullWidth value={draft.phone} onChange={set('phone')} disabled={!canEdit} /></Grid>
                  <Grid size={12}><TextField label="Puesto" placeholder="p. ej. Cajero, Gerente" fullWidth value={draft.role} onChange={set('role')} disabled={!canEdit} /></Grid>
                  {!member.is_patron && (
                    <Grid size={12}>
                      <FormControlLabel
                        control={<Switch checked={draft.is_admin} onChange={e => setDraft(d => d && { ...d, is_admin: e.target.checked })} disabled={!me?.is_patron || isSelf} />}
                        label={
                          <Box>
                            <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>Administrador</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Puede editar productos, inventario y al equipo.</Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', m: 0, '& .MuiSwitch-root': { mt: -0.5 } }}
                      />
                    </Grid>
                  )}
                </Grid>
              ) : <Skeleton variant="rounded" height={220} />}
            </SectionCard>
            {member && !isSelf && me?.is_admin && (!member.is_patron || me?.is_patron) && (
              <SessionsCard memberId={member.id} name={member.name || member.login} />
            )}
            {member && !member.is_patron && !isSelf && me?.is_admin && (
              <SectionCard title="Acceso">
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Quitar a esta persona le impide entrar a TRUE POS y a este panel. Sus ventas se conservan.
                </Typography>
                <Button color="error" variant="outlined" startIcon={<PersonRemoveOutlinedIcon />} onClick={() => setConfirmRemove(true)}>
                  Quitar de la organización
                </Button>
              </SectionCard>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard title="Ventas recientes" subtitle="Últimos 30 días como cajero" noPadding>
            {receipts.length === 0 ? (
              <EmptyState compact icon={<ReceiptLongOutlinedIcon />} title="Sin ventas en los últimos 30 días" />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Artículos</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.slice(0, 25).map(r => (
                    <TableRow key={r.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(r.purchase_date).format('D MMM, HH:mm')}</TableCell>
                      <TableCell>{r.checkout?.location?.name ?? '—'}</TableCell>
                      <TableCell>{number((r.items ?? []).length)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{currency(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmRemove}
        danger
        title={`¿Quitar a ${name}?`}
        description="Perderá acceso a TRUE POS y al panel de inmediato."
        confirmLabel="Quitar"
        onClose={() => setConfirmRemove(false)}
        onConfirm={async () => {
          await axios.delete(`management/members/${id}/`);
          navigate('/admin/organization', { replace: true });
        }}
      />
    </PageShell>
  );
};

export default MemberDetail;
